import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import LineCarIcon from 'LuxKit/Icons/line/LineCarIcon'
import LineChairWithAirplaneIcon from 'LuxKit/Icons/line/LineChairWithAirplaneIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineMinusIcon from 'LuxKit/Icons/line/LineMinusIcon'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineRedoIcon from 'LuxKit/Icons/line/LineRedoIcon'
import LineRoomUpgradeIcon from 'LuxKit/Icons/line/LineRoomUpgradeIcon'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import LineTaxiIcon from 'LuxKit/Icons/line/LineTaxiIcon'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Expander from 'components/utils/Expander'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOrder from 'hooks/Order/useOrder'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { dateIsAfterOrSame, dateIsBefore } from 'lib/datetime/dateUtils'
import LoyaltyPointsDeductionInfoModal from 'loyalty/components/LoyaltyPointsDeductionInfoModal'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo, useState } from 'react'
import styled from 'styled-components'
import { LUX_LOYALTY_POINTS_DEDUCTION_DATE } from '@luxuryescapes/lib-lux-loyalty'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import { useAppSelector } from 'hooks/reduxHooks'
import { LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE } from 'loyalty/constants/loyaltyConfig'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { isExpireWelcomePointsActivity } from 'api/mappers/loyalty/mapLoyaltyAccountActivity'

const ActivityRow = styled.div`
  display: grid;
  grid-template:
    "icon details details details expand-button"
    "icon date points status expand-button" auto / auto minmax(0, 1fr) ${rem(72)} ${rem(56)} min-content;
  gap: ${rem(4)} ${rem(8)};
  padding: ${rem(8)} ${rem(8)} ${rem(12)} 0;
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 0.2s;

  ${mediaQueryUp.tablet} {
    align-items: center;
    grid-template: "icon details date points status" auto / auto minmax(0, 1fr) ${rem(88)} ${rem(72)} ${rem(56)};
    gap: ${rem(8)};
    padding: ${rem(8)} 0 ${rem(12)};
  }

  &.expanded {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }
`

const ActivityIcon = styled.div`
  grid-area: icon;
  height: ${rem(32)};
  width: ${rem(32)};
  ${setSvgIconSize(16)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  display: grid;
  place-items: center;
  flex-shrink: 0;
`

const ActivityDetails = styled.div`
  grid-area: details;
`

const ActivityDate = styled.div`
  grid-area: date;
`

const ActivityPoints = styled.div`
  grid-area: points;
`

const ActivityStatus = styled.div`
  grid-area: status;
`

const ActivityExpandButton = styled.div`
  grid-area: expand-button;
  height: ${rem(20)};
  width: ${rem(20)};
  ${setSvgIconSize(20)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  display: grid;
  place-items: center;
  flex-shrink: 0;
  transition: background-color 200ms;

  &.expanded { background-color: ${props => props.theme.palette.neutral.default.eight}; }
  &.hidden { visibility: hidden; }

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const Loading = styled(LoadingBox)`
  grid-column: 1 / -1;
  grid-row: 1 / -1;

  height: ${rem(65)};

  ${mediaQueryUp.tablet} {
    height: ${rem(59)};
  }
`

interface Activity {
  icon?: React.ReactNode
  title: string
  subtitle?: string
  subtitleOnClick?: () => void
}

interface Props {
  activity: App.LoyaltyAccountActivity
}

function LoyaltyAccountLandingPageUserActivity(props: Props) {
  const { activity } = props
  const [order, fetchingOrder] = useOrder(activity.orderId)
  const [expanded, setExpanded] = useState<boolean>(false)
  const showModal = useContext(ModalContext)
  const hasLoyaltyPointsDeductionWarningEnabled = useAppSelector(state => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltyPointsDeductionWarning])

  const { icon, title, subtitle, subtitleOnClick } = useMemo((): Activity => {
    switch (activity.type) {
      case 'enrolment':
        return { title: 'Loyalty program enrolment' }
      case 'expired_points':
        return isExpireWelcomePointsActivity(activity.basePoints, activity.date) ? { title: 'Welcome points expired' } : { title: 'Points expired' }
      case 'manual':
        return { title: 'Manual adjustment' }
      case 'tier_change':
        return { title: 'Tier change', subtitle: activity.description }
      case 'expired_points_reversal':
        return { title: 'Points expired reversal' }
      case 'ad_hoc':
        return { title: activity.description ?? 'Other' }
      case 'other':
        return { title: activity.description ?? 'Other' }
      case 'points_ratio_adjust':
        return {
          title: 'Points adjustment',
          subtitle: 'View details',
          subtitleOnClick: () => showModal(<LoyaltyPointsDeductionInfoModal />),
        }
      case 'debit_points':
      case 'cancellation':
      case 'benefit_redemption':
      case 'earn_points':
      case 'spend_points':
      case 'earn_status_credits':
      case 'pending_points': {
        const benefitCode = order?.loyaltyDetails.benefitsRedeemed.find(benefit => benefit.itemId === activity.orderItemId)?.code
        const hotelItem = order?.items.find(item => item.id === activity.orderItemId)
        if (hotelItem) {
          return {
            icon: benefitCode === 'hotel_upgrade' ? <LineRoomUpgradeIcon /> : <SolidBedIcon />,
            title: benefitCode === 'hotel_upgrade' ? 'Room upgrade' : hotelItem.offer.property?.name!,
            subtitle: hotelItem.reservation?.roomType?.name,
          }
        }

        const bedbankItem = order?.bedbankItems.find(item => item.id === activity.orderItemId)
        if (bedbankItem) {
          return {

            icon: <SolidBedIcon />,
            title: bedbankItem.offer.property.name,
            subtitle: bedbankItem.offer.packages[0].rate.room.name,
          }
        }

        const experienceItem = order?.experienceItems.find(item => item.id === activity.orderItemId)
        if (experienceItem) {
          let icon = <LineHotAirBalloonIcon />
          if (benefitCode === 'airport_transfer') {
            icon = <LineTaxiIcon />
          } if (benefitCode === 'lounge_pass') {
            icon = <LineChairWithAirplaneIcon />
          }
          return {
            icon,
            title: activity.type === 'benefit_redemption' ? activity.description! : experienceItem.ticket.name,
            subtitle: activity.type === 'benefit_redemption' ? experienceItem.ticket.name : '',
          }
        }

        const cruiseItem = order?.cruiseItems.find(item => item.id === activity.orderItemId)
        if (cruiseItem) {
          return {
            icon: <LineShipIcon />,
            title: cruiseItem.offerName!,
          }
        }

        const insuranceItem = order?.insuranceItems.find(item => item.id === activity.orderItemId) || order?.bookingProtectionItems.find(item => item.id === activity.orderItemId)
        if (insuranceItem) {
          return { title: insuranceItem.productName }
        }

        const tourItem = order?.tourItems.find(item => item.id === activity.orderItemId)
        if (tourItem) {
          return {
            icon: <LineCompassIcon />,
            title: tourItem.tour.title,
          }
        }

        const tourExperienceItem = order?.tourExperienceItems.find(item => item.id === activity.orderItemId)
        if (tourExperienceItem) {
          return {
            icon: <LineCompassIcon />,
            title: tourExperienceItem.name,
          }
        }

        const flightItem = order?.flightItems.find(item => item.id === activity.orderItemId)
        if (flightItem) {
          return {

            icon: <LinePlaneDepartureIcon />,
            title: 'Flight',
            subtitle: flightItem.travellers?.map(traveller => `${traveller.givenName} ${traveller.surname}`).join(', '),
          }
        }

        const carHireItem = order?.carHireItems.find(item => item.id === activity.orderItemId)
        if (carHireItem) {
          return {
            icon: <LineCarIcon />,
            title: carHireItem.reservation?.vehicle.model ?? 'Car hire',
          }
        }

        const subscriptionItem = order?.subscriptionItems.find(item => item.id === activity.orderItemId)
        if (subscriptionItem) {
          if (activity.points < 0 || activity.statusCredits < 0) {
            return { title: `${LUXURY_PLUS.PROGRAM_NAME} renewal cancellation` }
          } else {
            return { title: `${LUXURY_PLUS.PROGRAM_NAME} membership` }
          }
        }

        const customOfferItem = order?.customOfferItems.find(item => item.id === activity.orderItemId)
        if (customOfferItem) {
          // Determine icon based on the type of the first item in the custom offer
          const itemType = customOfferItem.custom_offer.items?.[0]?.type
          let icon = <LineInfoCircleIcon />

          switch (itemType) {
            case 'hotel':
              icon = <SolidBedIcon />
              break
            case 'flight':
              icon = <LinePlaneDepartureIcon />
              break
            case 'cruise':
              icon = <LineShipIcon />
              break
            case 'tour':
              icon = <LineCompassIcon />
              break
            case 'experience':
              icon = <LineHotAirBalloonIcon />
              break
            case 'other':
              icon = <LineInfoCircleIcon />
              break
          }

          return {
            icon,
            title: customOfferItem.custom_offer.name,
          }
        }

        // fallbacks if we didn't find any order items to describe the activity types
        switch (activity.type) {
          case 'debit_points':
            return { title: activity.description!, subtitle: 'Points debited' }
          case 'pending_points':
            return { title: activity.description!, subtitle: 'Pending points' }
          case 'benefit_redemption':
            return { title: activity.description!, subtitle: 'Benefit redemption' }
          case 'earn_points':
            return { title: activity.description!, subtitle: 'Points earned' }
          case 'spend_points':
            return { title: activity.description!, subtitle: 'Points spent' }
          case 'earn_status_credits':
            return { title: activity.description!, subtitle: 'Status credits earned' }
          case 'cancellation':
            return { title: activity.description!, subtitle: 'Cancellation' }
        }
      }
    }
  }, [activity, order, showModal])

  const handleSubtitleOnClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation()
    subtitleOnClick?.()
  }, [subtitleOnClick])

  const isPending = activity.type === 'pending_points'
  const pointsIcon = useMemo(() => {
    if (isPending) {
      return <LineRedoIcon colour="warning" size={12} />
    } else if (activity.points > 0) {
      return <LinePlusIcon colour="success" size={12} />
    } else if (activity.points < 0) {
      return <LineMinusIcon colour="critical" size={12} />
    }
  }, [activity, isPending])

  const hasPendingPointsDeduction = isPending && dateIsBefore(new Date(activity.date), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE))

  const hasAccrualPointsDeduction =
    activity.type === 'debit_points' &&
    order &&
    // Booking date is before the points deduction date
    dateIsBefore(new Date(order.createdAt), new Date(LUX_LOYALTY_POINTS_DEDUCTION_DATE)) &&
    // Accrual happened after 27th June 2025 AEST
    dateIsAfterOrSame(new Date(activity.date), new Date(LOYALTY_ACCRUAL_DEDUCTION_CUT_OFF_DATE))

  const hasPointsDeduction =
    (hasPendingPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled) ||
    (hasAccrualPointsDeduction && hasLoyaltyPointsDeductionWarningEnabled)

  const handleShowPointsDeductionModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    e.stopPropagation()
    showModal(<LoyaltyPointsDeductionInfoModal />)
  }, [showModal])

  const isMobile = useScreenSizeOnly('mobile')

  const onRowClick = useCallback(() => {
    if (isMobile) {
      setExpanded(expanded => !expanded)
    }
  }, [isMobile])

  if (fetchingOrder) {
    return <ActivityRow><Loading /></ActivityRow>
  }

  const trulyExpandable = !!(subtitle || isPending || activity.type === 'benefit_redemption')
  const theTrueExpanded = trulyExpandable && isMobile && expanded

  return <ActivityRow
    as={isMobile ? Clickable : undefined}
    onClick={isMobile ? onRowClick : undefined}
    className={theTrueExpanded ? 'expanded' : undefined}
  >
    <ActivityIcon>{icon || <LineInfoCircleIcon />}</ActivityIcon>
    <ActivityDetails>
      <Group direction="vertical" gap={2}>
        <BodyText variant="medium" colour="neutral-two" wrap={!expanded ? 'truncate' : undefined}>
          {title}
        </BodyText>
        {!!subtitle && <CSSBreakpoint min="tablet">
          <Group direction="horizontal" gap={8}>
            {!subtitleOnClick && <BodyText variant="small" colour="neutral-two" wrap={!expanded ? 'truncate' : undefined}>
              {subtitle}
            </BodyText>}
            {!!subtitleOnClick && <TextLink size="small" weight="regular" wrap={!expanded ? 'truncate' : undefined} onClick={handleSubtitleOnClick}>
              {subtitle}
            </TextLink>}
            {hasPointsDeduction && <TextLink size="small" weight="regular" onClick={handleShowPointsDeductionModal}>Points adjusted</TextLink>}
          </Group>
        </CSSBreakpoint>}
      </Group>
      {trulyExpandable && <Expander expanded={theTrueExpanded}>
        <Group direction="vertical" gap={4} horizontalAlign="start">
          {!!subtitle && <Group direction="horizontal" gap={8}>
            {!subtitleOnClick && <BodyText variant="small" colour="neutral-two">{subtitle}</BodyText>}
            {!!subtitleOnClick && <TextLink size="small" weight="regular" onClick={handleSubtitleOnClick}>{subtitle}</TextLink>}
            {hasPointsDeduction && <TextLink size="small" weight="regular" onClick={handleShowPointsDeductionModal}>Points adjusted</TextLink>}
          </Group>}
          <LabelGroup>
            {isPending && <StatusLabel variant="pending">Pending</StatusLabel>}
            {activity.type === 'benefit_redemption' && <StatusLabel variant="confirmation">Redeemed</StatusLabel>}
          </LabelGroup>
        </Group>
      </Expander>}
    </ActivityDetails>
    <ActivityDate>
      <BodyText variant="medium" colour="neutral-three">
        <FormatDateTime value={activity.date} format="dmy-short" />
      </BodyText>
    </ActivityDate>
    <ActivityPoints>
      <BodyTextBlock
        variant="medium"
        startIcon={pointsIcon}
        colour={isPending ? 'neutral-three' : 'neutral-two'}
        align="end"
      >
        {!!activity.points && <FormatDecimal value={Math.abs(activity.points)} />}
        {!activity.points && '-'}
      </BodyTextBlock>
    </ActivityPoints>
    <ActivityStatus>
      <BodyText variant="medium" colour="neutral-two" align="end">
        {activity.statusCredits ? <FormatDecimal value={activity.statusCredits} /> : '-'}
      </BodyText>
    </ActivityStatus>
    <ActivityExpandButton className={clsx({ expanded: theTrueExpanded, hidden: !trulyExpandable })}>
      <DropdownChevron open={theTrueExpanded} />
    </ActivityExpandButton>
  </ActivityRow>
}

export default LoyaltyAccountLandingPageUserActivity
